import React, { useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { BsPlusCircleFill } from "react-icons/bs";
import { AddDiscovery } from "./AddDiscovery";
import { useDiscoveryColumn } from "./hooks";
import { useListApi } from "@/hooks/useListApi";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { CustomPageSelector, CustomPagination } from "../CustomPagination";
import { CustomPaginationInfo } from "../CustomPaginationInfo";
import { Discovery, discoveryApi } from "@/api/discovery";
import { AddCaseSourceFromDiscovery } from "../Sources/AddCaseSourceFromDiscovery";
import { usePermission } from "@/hooks/usePermission";
import { useTranslation } from "react-i18next";

function DiscoveryTable({ caseId }: { caseId: number }) {
  const { t } = useTranslation();
  const { loading, refresh, apiResult, search, pagination, order } =
    useListApi<Discovery>({
      baseApiObject: discoveryApi,
      defaultParams: { case: caseId },
      enable: true,
    });

  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [openFromDiscovery, setOpenFromDiscovery] = useState<boolean>(false);
  const [discoveryItem, setDiscoveryItem] = useState<Discovery>();
  const hasAddDiscoveryPermission = usePermission("discovery.add_discovery");

  const columns = useDiscoveryColumn({
    refresh,
    setOrder: order.set,
    caseId: caseId,
    handleAddFromDiscovery: (item: Discovery) => {
      setDiscoveryItem(item);
      setOpenFromDiscovery(true);
    },
  });

  return (
    <div className="w-full my-8">
      <div className="flex justify-between items-center">
        {hasAddDiscoveryPermission && (
          <AddDiscovery
            open={openCreate}
            caseId={caseId}
            setOpen={setOpenCreate}
            trigger={
              <Button className="flex gap-2">
                <BsPlusCircleFill /> {t("discovery.button.upload")}
              </Button>
            }
            refresh={refresh}
          />
        )}
        {!!discoveryItem && (
          <AddCaseSourceFromDiscovery
            caseId={caseId}
            discovery={discoveryItem}
            open={openFromDiscovery}
            setOpen={setOpenFromDiscovery}
            refresh={refresh}
          />
        )}
      </div>
      <div className="flex gap-2 w-full my-4 md:w-[410px]">
        <Input
          value={search.value}
          type="text"
          placeholder={t("common.placeholder.search")}
          onChange={(event) => search.set(event.target.value)}
        />
        {search.value && (
          <Button onClick={() => search.set("")}>
            {t("common.button.clear")}
          </Button>
        )}
      </div>
      {loading ? (
        <div className="flex min-h-[300px] md:min-h-[450px] items-center justify-center max-h-auto">
          <Loader />
        </div>
      ) : (
        <>
          <div className="min-h-[300px] md:min-h-[450px] max-h-auto">
            <DataTable columns={columns} data={apiResult?.results || []} />
            <div className="flex flex-col md:flex-row md:justify-between">
              <div className="flex flex-col md:flex-row md:w-2/3   md:space-x-4 mt-3">
                <CustomPageSelector
                  pageSize={pagination.pageSize}
                  onPageSizeChange={(value) => {
                    pagination.setPageSize(value);
                    pagination.setPage(1);
                  }}
                  persistenceKey={`discoveryPageSize_${caseId}`}
                  persistPagination={true}
                />
                <CustomPaginationInfo
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                />
              </div>
              <div className="flex md:space-x-4 mt-4 pr-5 pt-2">
                <CustomPagination
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                  onPageChange={(value) => pagination.setPage(value)}
                  persistenceKey={`discoveryPage_${caseId}`}
                  persistPagination={true}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DiscoveryTable;
